/**
 * Vendor-side panel — pending dashboard-access requests from admins/agents,
 * currently-active grants (revocable), and a tail of the audit log.
 *
 * Mounted on the Vendor Dashboard. Hidden when there's nothing to show, so
 * vendors with no pending or active access don't see an empty card cluttering
 * the page.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ShieldQuestion, Check, X, Eye, Trash2 } from "lucide-react";
import { toast } from "sonner";

function _fmtAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function VendorAccessInboxCard({ onChanged }) {
  const [grants, setGrants] = useState([]);
  const [log, setLog] = useState([]);
  const [showLog, setShowLog] = useState(false);
  const [busy, setBusy] = useState(null); // grant id being acted on

  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get("/vendor-access/inbox"),
        api.get("/vendor-access/log"),
      ]);
      setGrants(a.data || []);
      setLog(b.data || []);
    } catch (e) {
      // Non-fatal: card just stays empty.
      console.error("vendor-access load failed:", e);
    }
  };

  useEffect(() => { load(); }, []);

  const decide = async (id, decision, expires_in_days = 7) => {
    setBusy(id);
    try {
      await api.post(`/vendor-access/${id}/decide`, { decision, expires_in_days });
      toast.success(decision === "approved" ? "Access approved" : "Request denied");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const revoke = async (id) => {
    setBusy(id);
    try {
      await api.post(`/vendor-access/${id}/revoke`);
      toast.success("Access revoked");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const pending = grants.filter((g) => g.status === "pending");
  const approved = grants.filter((g) => g.status === "approved");

  if (pending.length === 0 && approved.length === 0 && log.length === 0) return null;

  return (
    <div className="rounded-2xl border border-border bg-white p-4 sm:p-5 shadow-sm" data-testid="vendor-access-inbox-card">
      <div className="flex items-center gap-2 mb-3">
        <ShieldQuestion className="h-4 w-4 text-blue-600" />
        <h2 className="font-display text-lg">Dashboard access requests</h2>
      </div>
      <p className="text-xs text-neutral-500 mb-4">
        Admins or agents who need to view (or add vehicles to) your dashboard request your permission first.
        Grants expire automatically; you can revoke them any time.
      </p>

      {pending.length > 0 && (
        <div className="space-y-2 mb-4" data-testid="vendor-access-pending-list">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">Pending</div>
          {pending.map((g) => (
            <div key={g.id} className="rounded-xl border border-amber-200 bg-amber-50/50 px-3 py-2.5 flex flex-wrap items-center justify-between gap-2" data-testid={`grant-pending-${g.id}`}>
              <div className="min-w-0 text-sm">
                <div className="font-medium">{g.viewer_name || g.viewer_email} <span className="text-xs text-neutral-500">· {g.viewer_role}</span></div>
                {g.reason && <div className="text-xs text-neutral-600 italic">"{g.reason}"</div>}
                <div className="text-[11px] text-neutral-400">Requested {_fmtAgo(g.requested_at)}</div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <Button size="sm" variant="ghost" onClick={() => decide(g.id, "denied")} disabled={busy === g.id} className="rounded-full text-xs h-7" data-testid={`grant-deny-${g.id}`}>
                  <X className="h-3.5 w-3.5 mr-1" />Deny
                </Button>
                <Button size="sm" onClick={() => decide(g.id, "approved", 7)} disabled={busy === g.id} className="rounded-full text-xs h-7 bg-emerald-600 hover:bg-emerald-700" data-testid={`grant-approve-${g.id}`}>
                  <Check className="h-3.5 w-3.5 mr-1" />Approve · 7 days
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {approved.length > 0 && (
        <div className="space-y-2 mb-3" data-testid="vendor-access-approved-list">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">Active grants</div>
          {approved.map((g) => (
            <div key={g.id} className="rounded-xl border border-emerald-200 bg-emerald-50/40 px-3 py-2 flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`grant-active-${g.id}`}>
              <div className="min-w-0">
                <div className="font-medium">{g.viewer_name || g.viewer_email}</div>
                <div className="text-[11px] text-neutral-500">
                  Expires {_fmtAgo(g.expires_at)} · {g.access_count} access{g.access_count === 1 ? "" : "es"}
                </div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => revoke(g.id)} disabled={busy === g.id} className="rounded-full text-xs h-7 text-rose-700 hover:bg-rose-50" data-testid={`grant-revoke-${g.id}`}>
                <Trash2 className="h-3.5 w-3.5 mr-1" />Revoke
              </Button>
            </div>
          ))}
        </div>
      )}

      {log.length > 0 && (
        <div data-testid="vendor-access-log-section">
          <button
            type="button"
            onClick={() => setShowLog((v) => !v)}
            className="inline-flex items-center gap-1.5 text-xs text-neutral-600 hover:text-neutral-900"
            data-testid="vendor-access-log-toggle"
          >
            <Eye className="h-3.5 w-3.5" />
            {showLog ? "Hide" : "Show"} access log ({log.length})
          </button>
          {showLog && (
            <ul className="mt-2 space-y-1 max-h-56 overflow-y-auto text-xs text-neutral-600">
              {log.slice(0, 100).map((e, i) => (
                <li key={`${e.at}-${i}`} className="font-mono">
                  {_fmtAgo(e.at)} · {e.viewer_role} · <span className="text-neutral-500">{e.path}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
